import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export interface User {
  id: string;
  email: string;
  name?: string;
  avatarUrl?: string;
  teamId?: number;
}

export interface AuthState {
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  expiresAt: number | null;
}

export interface AuthActions {
  setUser: (user: User) => void;
  setTokens: (accessToken: string, refreshToken?: string, expiresAt?: number) => void;
  setAuth: (user: User, accessToken: string, refreshToken?: string, expiresAt?: number) => void;
  logout: () => void;
  setLoading: (loading: boolean) => void;
  clearAuth: () => void;
  isTokenExpired: () => boolean;
  isTokenExpiringSoon: () => boolean;
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: true,
  expiresAt: null,
};

export const useAuthStore = create<AuthState & AuthActions>()(
  persist(
    (set, get) => ({
      ...initialState,

      setUser: (user: User) => {
        set({ user });
      },

      setTokens: (accessToken: string, refreshToken?: string, expiresAt?: number) => {
        set({
          token: accessToken,
          refreshToken: refreshToken || get().refreshToken,
          expiresAt: expiresAt || Date.now() + 15 * 60 * 1000, // Default 15 minutes
          isAuthenticated: true,
        });
      },

      setAuth: (user: User, accessToken: string, refreshToken?: string, expiresAt?: number) => {
        set({
          user,
          token: accessToken,
          refreshToken: refreshToken || get().refreshToken,
          expiresAt: expiresAt || Date.now() + 15 * 60 * 1000,
          isAuthenticated: true,
          isLoading: false,
        });
      },

      logout: () => {
        set({
          ...initialState,
          isLoading: false,
        });
        // Clear persisted state
        localStorage.removeItem('leadgpt-auth-storage');
      },

      setLoading: (loading: boolean) => {
        set({ isLoading: loading });
      },

      clearAuth: () => {
        set({
          ...initialState,
          isLoading: false,
        });
      },

      isTokenExpired: (): boolean => {
        const { expiresAt } = get();
        if (!expiresAt) return true;
        return Date.now() >= expiresAt;
      },

      isTokenExpiringSoon: (): boolean => {
        const { expiresAt } = get();
        if (!expiresAt) return true;
        // Consider token expiring soon if less than 5 minutes remaining
        return Date.now() >= expiresAt - 5 * 60 * 1000;
      },
    }),
    {
      name: 'leadgpt-auth-storage',
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        refreshToken: state.refreshToken,
        isAuthenticated: state.isAuthenticated,
        expiresAt: state.expiresAt,
      }),
      onRehydrateStorage: () => (state) => {
        if (state) {
          // Check if token is expired on rehydration
          if (state.isTokenExpired()) {
            state.clearAuth();
          } else {
            state.setLoading(false);
          }
        }
      },
    },
  ),
);

// Selectors for easier usage
export const useAuth = () => {
  const {
    user,
    token,
    refreshToken,
    isAuthenticated,
    isLoading,
    expiresAt,
    setUser,
    setTokens,
    setAuth,
    logout,
    setLoading,
    clearAuth,
    isTokenExpired,
    isTokenExpiringSoon,
  } = useAuthStore();

  return {
    user,
    token,
    refreshToken,
    isAuthenticated,
    isLoading,
    expiresAt,
    setUser,
    setTokens,
    setAuth,
    logout,
    setLoading,
    clearAuth,
    isTokenExpired,
    isTokenExpiringSoon,
  };
};